// src/components/audition-booking-wizard/utils/videoCompression.js

export const MAX_SIZE_MB = 50;
export const MAX_DURATION_SEC = 300; // 5 minutes
export const COMPRESS_THRESHOLD_MB = 25;

/**
 * Client-side video compression using MediaRecorder.
 * @param {File} file - The original video file.
 * @param {function} setCompressProgress - Callback to update compression progress (0-100).
 * @returns {Promise<Blob>} - Compressed video as a Blob.
 */
export const clientCompress = async (file, setCompressProgress) => {
    return new Promise(async (resolve, reject) => {
        try {
            const video = document.createElement("video");
            video.src = URL.createObjectURL(file);
            video.muted = true;
            video.playsInline = true;

            video.onloadedmetadata = async () => {
                await video.play();

                const stream = video.captureStream();

                const recorder = new MediaRecorder(stream, {
                    mimeType: "video/webm;codecs=vp9,opus", // VP9/Opus for good compression and browser support
                    videoBitsPerSecond: 300000 // Adjust this for desired quality/size
                });

                const chunks = [];

                recorder.ondataavailable = (e) => {
                    if (e.data.size) chunks.push(e.data);
                };

                recorder.start();

                const timer = setInterval(() => {
                    if (video.duration) { // Ensure duration is available
                        const percent = Math.min(
                            Math.round((video.currentTime / video.duration) * 100),
                            99
                        );
                        setCompressProgress(percent);
                    }
                }, 500);

                video.onended = () => {
                    recorder.stop();
                    clearInterval(timer);
                };

                recorder.onstop = () => {
                    setCompressProgress(100);
                    const blob = new Blob(chunks, { type: "video/webm" });
                    resolve(blob);
                };
            };

            video.onerror = (e) => {
                reject(new Error("Error loading video for compression: " + e.message));
            };

        } catch (err) {
            reject(err);
        }
    });
};